<template>
	<div>
		<HeaderMain v-if="!isServerError" />
		<div
			class="pt-16 pb-14 md:py-20"
			:class="{'min-h-[100vh] flex items-center': isServerError}"
			:style="backgroundStyles"
		>
			<div class="container">
				<div
					class="mx-auto px-2 py-8 rounded-2.5xl bg-background-primary text-center md:px-8 md:py-12"
					:class="error.statusCode === 429 ? 'max-w-[37.5rem]' : 'max-w-[31.9375rem]'"
				>
					<h1 class="text-price font-bold font-secondary text-heading-secondary leading-tight" v-text="message.title" />
					<div class="mt-8">
						<p
							v-if="message.description"
							class="text-body-m sm:text-xl text-body-primary"
							v-html="message.description"
						/>

						<span
							v-if="errorId"
							class="block text-center text-body-tertiary text-sm mt-6"
							:class="{'mb-8': isServerError}"
						>
							ID chyby: {{ errorId }}
						</span>
					</div>

					<div v-if="isServerError" class="flex gap-4 justify-center mt-8">
						<button class="btn btn-primary btn-xl" @click="reloadPage">
							Obnovit stránku
						</button>
						<NuxtLink to="/" class="btn btn-secondary btn-xl" external>
							<NuxtIcon name="custom/arrow-right" aria-hidden="true" class="text-2xl" filled />
							Úvodní strana
						</NuxtLink>
					</div>
				</div>
			</div>
			<div v-if="!isServerError" class="container pt-10 pb-20 md:pt-20">
				<h2 class="typo-h1 text-heading-inverse mb-4 md:mb-8">
					Kam dál
				</h2>
				<RoundedPresentationalTilesSkeleton>
					<template #default>
						<RoundedPresentationalTileSkeleton variant="light">
							<template #image>
								<NuxtPicture
									src="/images/errorPage/recommendation-2.jpg"
									alt="Hlavní stránka"
									sizes="xs:175px sm:334px md:113px lg:187px xl:208px xxl:304px inf:304px"
									:img-attrs="{class:'w-full h-full object-cover d-block'}"
								/>
							</template>
							<template #title>
								<AbsoluteInternalLink
									to="/"
									class="before:cover-parent"
								>
									Hlavní stránka
								</AbsoluteInternalLink>
							</template>
						</RoundedPresentationalTileSkeleton>
						<RoundedPresentationalTileSkeleton variant="light">
							<template #image>
								<NuxtPicture
									src="/images/errorPage/recommendation-1.jpg"
									alt="Akční nabídka"
									sizes="xs:175px sm:334px md:113px lg:187px xl:208px xxl:304px inf:304px"
									:img-attrs="{class:'w-full h-full object-cover d-block'}"
								/>
							</template>
							<template #title>
								<AbsoluteInternalLink
									:to="branchSpecificPath({name: 'hypermarket-akcni-nabidka-category'})"
									class="before:cover-parent"
								>
									Akční nabídka
								</AbsoluteInternalLink>
							</template>
						</RoundedPresentationalTileSkeleton>
						<RoundedPresentationalTileSkeleton variant="light">
							<template #image>
								<NuxtPicture
									src="/images/errorPage/recommendation-3.jpg"
									alt="Blog"
									sizes="xs:175px sm:334px md:113px lg:187px xl:208px xxl:304px inf:304px"
									:img-attrs="{class:'w-full h-full object-cover d-block'}"
								/>
							</template>
							<template #title>
								<AbsoluteInternalLink
									to="/blog"
									class="before:cover-parent"
								>
									Blog
								</AbsoluteInternalLink>
							</template>
						</RoundedPresentationalTileSkeleton>
						<RoundedPresentationalTileSkeleton variant="light">
							<template #image>
								<NuxtPicture
									src="/images/errorPage/recommendation-4.jpg"
									alt="Recepty"
									sizes="xs:175px sm:334px md:113px lg:187px xl:208px xxl:304px inf:304px"
									:img-attrs="{class:'w-full h-full object-cover d-block'}"
								/>
							</template>
							<template #title>
								<AbsoluteInternalLink
									to="/recepty"
									class="before:cover-parent"
								>
									Recepty
								</AbsoluteInternalLink>
							</template>
						</RoundedPresentationalTileSkeleton>
					</template>
				</RoundedPresentationalTilesSkeleton>
			</div>
		</div>
		<FooterMain v-if="!isServerError" shift-up />
	</div>
</template>

<script setup lang="ts">
	import {createMarketingHeadData, trackPageViewError} from '~/utils/tracking';

	interface Props {
		error: {
			url: string,
			statusCode: number,
			statusMessage: string,
			message: string,
			stack: string,
			data?: string | Record<string, unknown>,
			applicationErrorId?: string,
		}
	}
	const props = defineProps<Props>();

	const isUnauthorizedRedirect = import.meta.client && props.error.statusCode === 401;
	if (isUnauthorizedRedirect) {
		clearError({redirect: '/prihlaseni'});
	}

	const {t} = useI18n();
	const img = useImage();
	const runtimeConfig = useRuntimeConfig();
	const {isSpeedMode} = useSpeedMode();
	const branchSpecificPath = useBranchSpecificPath();

	const {scripts: gtmScripts, noscript: gtmNoscript} = createMarketingHeadData(runtimeConfig, isSpeedMode);

	useHead({
		script: gtmScripts,
		noscript: gtmNoscript,
	});

	const isServerError = computed(() => {
		return props.error.statusCode >= 500 || props.error.statusCode === 429;
	});

	const message = computed(() => {
		if (isServerError.value) {
			if (props.error.statusCode === 429) {
				return {
					title: t('common.error.429.title'),
					description: t('common.error.429.description'),
				};
			}

			return {
				title: t('common.error.500.title'),
				description: t('common.error.500.description', {br: '<br />'}),
			};
		}

		switch (props.error.statusCode) {
			case 404:
				return {
					title: t('common.error.404.title'),
					description: t('common.error.404.description', {br: '<br />'}),
				};
			default:
				return {
					title: props.error.message,
					description: null,
				};
		}
	});

	const backgroundStyles = computed(() => {
		const imgPath = isServerError.value ? '/images/errorPage/500-bg.jpg' : '/images/errorPage/404-bg.jpg';
		const url = img(imgPath, {format: 'webp', width: 1920}, {sizes: 'xs:100vw sm:100vw md:100vw lg:100vw xl:100vw xxl:100vw'});

		return {
			backgroundImage: `url('${url}')`,
			backgroundRepeat: 'no-repeat',
			backgroundPosition: 'top center',
			backgroundSize: 'cover',
		};
	});

	const parsedErrorData = computed<Record<string, unknown> | null>(() => {
		if (!props.error.data) {
			return null;
		}

		if (typeof props.error.data === 'object') {
			return props.error.data;
		}

		const rawData = props.error.data.trim();

		if (!rawData.startsWith('{')) {
			return null;
		}

		try {
			const parsedData = JSON.parse(rawData);

			return parsedData && typeof parsedData === 'object' && !Array.isArray(parsedData)
				? parsedData
				: null;
		} catch {
			return null;
		}
	});

	const errorId = computed(() => {
		const applicationErrorId = parsedErrorData.value?.applicationErrorId;

		return typeof applicationErrorId === 'string'
			? applicationErrorId
			: props.error.applicationErrorId ?? null;
	});

	const reloadPage = () => {
		window.location.reload();
	};

	onMounted(() => {
		if (isUnauthorizedRedirect) {
			return;
		}

		trackPageViewError({
			url: props.error.url,
			statusCode: props.error.statusCode,
			statusMessage: props.error.statusMessage,
			message: props.error.message,
			stack: props.error.stack,
		});
	});
</script>
